export class TableKeyboardHandler {
    private _keydownHandler: (event: KeyboardEvent) => void;
    private _focusinHandler?: (event: FocusEvent) => void;
    private _observer?: MutationObserver;
    private _dynamicRowsObserver?: MutationObserver;
    private _devMode: boolean;
    private _lastFocusedRow: HTMLElement | null = null;
    private _containerEl: HTMLElement;
    private _bodyClass = 'keyboard-focus-mode';
    private _tabPressedOnce = false;

    constructor(containerEl: HTMLElement, dev: boolean = false) {
        this._containerEl = containerEl;
        this._devMode = dev;
        
        this._keydownHandler = this.OnKeyDown.bind(this);
        document.addEventListener('keydown', this._keydownHandler);

        if (this._devMode) {
            this._focusinHandler = this.OnFocusIn.bind(this);
            document.addEventListener('focusin', this._focusinHandler);
        }

        this.AddTabindexToRows(this._containerEl);
        this.ObserveDynamicRows();
    }

    private OnKeyDown(event: KeyboardEvent): void {
        if (!this._tabPressedOnce && event.code === 'Tab') {
            this._tabPressedOnce = true;
            document.body.classList.add(this._bodyClass);
        }

        const active = document.activeElement as HTMLElement | null;
        if (!active) {
            return;
        }

        if (active.tagName === 'TR' && this._containerEl.contains(active)) {
            const checkbox = active.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

            if (event.code === 'Space') {
                if (checkbox) {
                    event.preventDefault();
                    checkbox.click();
                    this._lastFocusedRow = active;
                    this.WaitForPopupButtons();
                }
            } 
            else if (event.code === 'Enter') {
                if (checkbox && this.IsAnyCheckboxChecked()) {
                    event.preventDefault();
                    checkbox.click();
                    this._lastFocusedRow = active;
                    this.WaitForPopupButtons();
                }
            } 
            else if (event.code === 'ControlLeft') {
                const expandAnchor = active.querySelector('td.action-column .toggle-expand-button') as HTMLAnchorElement | null;

                if (expandAnchor && !expandAnchor.classList.contains('disable')) {
                    event.preventDefault();
                    expandAnchor.click();
                }
            }
        }
        
        if (event.code === 'Enter') {
            if (active.tagName === 'BUTTON' && this.IsInPopupButtonRow(active)) {
                event.preventDefault();
                active.click();

                setTimeout(() => {
                    if (this._lastFocusedRow && document.body.contains(this._lastFocusedRow)) {
                        this._lastFocusedRow.focus();
                    }
                }, 100);
            }
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            this.HandleArrowNavigationOnPopupButtons(event);
        }
    }

    private IsAnyCheckboxChecked(): boolean {
        const checkboxes = this._containerEl.querySelectorAll('tr input[type="checkbox"]');
        return Array.from(checkboxes).some((cb) => (cb as HTMLInputElement).checked);
    }

    private IsInPopupButtonRow(el: HTMLElement): boolean {
        return el.closest('.confirmation-popup-btn-wrapper') !== null;
    }

    private WaitForPopupButtons(): void {
        if (this._observer) {
            this._observer.disconnect();
        }

        this._observer = new MutationObserver((mutations, observer) => {
            const popupButtons = document.querySelector('.confirmation-popup-btn-wrapper');
            if (popupButtons) {
                const buttons = popupButtons.querySelectorAll('button');
                const visibleButton = Array.from(buttons).find(btn => {
                    const style = window.getComputedStyle(btn);
                    return style.display !== 'none' && style.visibility !== 'hidden';
                }) as HTMLButtonElement | undefined;

                if (visibleButton) {
                    visibleButton.focus();
                    observer.disconnect();
                }
            }
        });

        this._observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    private OnFocusIn(event: FocusEvent): void {
        const el = event.target as HTMLElement;

        let selector = el.tagName.toLowerCase();
        if (el.id){
            selector += `#${el.id}`;
        }
        if (el.classList.length > 0){
            selector += '.' + [...el.classList].join('.');   
        }
        const parent = el.parentElement;
        if (parent) {
            const children = Array.from(parent.children);
            const index = children.indexOf(el) + 1;
            selector += `:nth-child(${index})`;
        }
    }

    private HandleArrowNavigationOnPopupButtons(event: KeyboardEvent): void {
        const wrapper = document.querySelector('.confirmation-popup-btn-wrapper');
        if (!wrapper) return;

        const buttons = Array.from(wrapper.querySelectorAll('button')) as HTMLButtonElement[];
        const active = document.activeElement as HTMLElement;

        if (!buttons.includes(active as HTMLButtonElement)) return;

        const currentIndex = buttons.indexOf(active as HTMLButtonElement);
        let target: HTMLButtonElement | undefined;

        if (event.key === 'ArrowRight') {
            target = buttons[currentIndex + 1] || buttons[0];
        } else if (event.key === 'ArrowLeft') {
            target = buttons[currentIndex - 1] || buttons[buttons.length - 1];
        }

        if (target) {
            target.focus();
            event.preventDefault();
        }
    }

    private AddTabindexToRows(root: HTMLElement): void {
        const rows = root.matches('tr') ? [root] : Array.from(root.querySelectorAll('tr'));
        for (const row of rows) {
            if (row.closest('thead')) {
                continue;
            }

            row.setAttribute('tabindex', '1');
        }
    }

    private ObserveDynamicRows(): void {
        this._dynamicRowsObserver = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        this.AddTabindexToRows(node);
                    }
                });
            }
        });

        this._dynamicRowsObserver.observe(this._containerEl, {
            childList: true,
            subtree: true,
        });
    }

    public Dispose(): void {
        document.removeEventListener('keydown', this._keydownHandler);
        if (this._devMode && this._focusinHandler) {
            document.removeEventListener('focusin', this._focusinHandler);
        }
        if (this._observer) {
            this._observer.disconnect();
        }
        if (this._dynamicRowsObserver) {
            this._dynamicRowsObserver.disconnect();
        }
        document.body.classList.remove(this._bodyClass);
    }
}
